/**
 * Watchlist ("watch later") — hybrid store.
 * See favorites.ts for the sign-in migration model.
 */
import { useEffect, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { getDb } from "./firebase";

const KEY = "rawan-watchlist-v1";

export type WatchlistRef = { slug: string; kind: "movie" | "series"; addedAt: number };

type Listener = () => void;
const listeners = new Set<Listener>();

let _uid: string | null = null;
let _remote: WatchlistRef[] = [];
let _unsub: (() => void) | null = null;

function readLocal(): WatchlistRef[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as WatchlistRef[]) : [];
  } catch {
    return [];
  }
}
function writeLocal(next: WatchlistRef[]): void {
  try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* ignore */ }
}
function notify(): void { listeners.forEach((l) => l()); }

export function setActiveUid(uid: string | null): void {
  if (uid === _uid) return;
  if (_unsub) { try { _unsub(); } catch { /* ignore */ } _unsub = null; }
  _uid = uid;
  _remote = [];

  const db = getDb();
  if (!uid || !db) { notify(); return; }

  const local = readLocal();
  if (local.length > 0) {
    const batch = writeBatch(db);
    for (const f of local) {
      batch.set(
        doc(db, "users", uid, "watchlist", f.slug),
        { kind: f.kind, addedAt: f.addedAt || Date.now() },
        { merge: true },
      );
    }
    batch.commit().then(() => {
      try { localStorage.removeItem(KEY); } catch { /* ignore */ }
    }).catch(() => { /* ignore */ });
  }

  const c = collection(db, "users", uid, "watchlist");
  _unsub = onSnapshot(query(c, orderBy("addedAt", "desc"), limit(500)), (snap) => {
    const out: WatchlistRef[] = [];
    snap.forEach((s) => {
      const d = s.data() as { kind?: "movie" | "series"; addedAt?: number };
      out.push({ slug: s.id, kind: (d.kind ?? "movie"), addedAt: d.addedAt ?? 0 });
    });
    _remote = out;
    notify();
  }, () => { /* ignore */ });
}

export function getWatchlist(): WatchlistRef[] {
  return _uid ? _remote : readLocal();
}
export function isOnWatchlist(slug: string): boolean {
  return getWatchlist().some((f) => f.slug === slug);
}
export function toggleWatchlist(slug: string, kind: WatchlistRef["kind"]): boolean {
  const uid = _uid;
  const db = getDb();
  if (uid && db) {
    const ref = doc(db, "users", uid, "watchlist", slug);
    const exists = _remote.some((f) => f.slug === slug);
    if (exists) {
      _remote = _remote.filter((f) => f.slug !== slug);
      notify();
      deleteDoc(ref).catch(() => {});
      return false;
    }
    _remote = [{ slug, kind, addedAt: Date.now() }, ..._remote];
    notify();
    setDoc(ref, { kind, addedAt: Date.now() }).catch(() => {});
    return true;
  }
  const cur = readLocal();
  const exists = cur.find((f) => f.slug === slug);
  if (exists) {
    writeLocal(cur.filter((f) => f.slug !== slug));
    notify();
    return false;
  }
  writeLocal([{ slug, kind, addedAt: Date.now() }, ...cur]);
  notify();
  return true;
}

export function useWatchlist(): WatchlistRef[] {
  const [, tick] = useState(0);
  useEffect(() => {
    const l: Listener = () => tick((n) => n + 1);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);
  return getWatchlist();
}
